import { obtainFreshAccessViaRefresh } from '../api/authRefresh';
import { AssetRequestError } from '../api/getAssetById';
import { getAssetById } from '../api/getAssetById';
import {
  addFavourite,
  fetchFavourites,
  type FavouriteWireRow,
} from '../api/favouritesApi';
import { isAccessTokenValidForApi } from '../api/withApiAuth';
import { getSessionToken, persistAccessTokenOnly } from '../auth/sessionToken';
import { coalesceAssetListRecord } from '../utils/assetListNormalize';
import { assetRecordMutationId, assetRecordPrimaryId } from '../utils/assetRecordPrimaryId';
import {
  readAllLocalFavoriteEntries,
  readPendingFavoriteSyncIds,
  removePendingFavoriteSyncIds,
  type FavoriteAssetEntry,
} from './favoriteAssetsStorage';
import { localFavouriteEntriesMissingOnServer } from './favoriteAssetsSync';

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isUuidLikeAssetId(value: string): boolean {
  const trimmed = value.trim();
  return (
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(trimmed) ||
    /^[0-9a-f]{32}$/i.test(trimmed)
  );
}

/** Prefer numeric / legacy ids before UUIDs — POST /favourites often rejects UUID path params. */
function rankFavouritePostCandidates(ids: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of ids) {
    const trimmed = raw.trim();
    if (!trimmed) {
      continue;
    }
    const key = trimmed.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    out.push(trimmed);
  }
  return out.sort((a, b) => {
    const aUuid = isUuidLikeAssetId(a);
    const bUuid = isUuidLikeAssetId(b);
    if (aUuid === bUuid) {
      return 0;
    }
    return aUuid ? 1 : -1;
  });
}

function pendingEntriesFromIds(ids: readonly string[]): FavoriteAssetEntry[] {
  return ids.map((id) => ({
    id,
    asset: { asset_id: id, id },
    savedAt: Date.now(),
  }));
}

function combineEntriesForFlush(): FavoriteAssetEntry[] {
  const seen = new Set<string>();
  const out: FavoriteAssetEntry[] = [];
  for (const entry of [...readAllLocalFavoriteEntries(), ...pendingEntriesFromIds(readPendingFavoriteSyncIds())]) {
    const id = entry.id.trim();
    if (!id || seen.has(id.toLowerCase())) {
      continue;
    }
    seen.add(id.toLowerCase());
    out.push(entry);
  }
  return out;
}

/** Ensure a usable access token exists before POST/DELETE favourites. */
export async function ensureAccessTokenForFavoriteFlush(): Promise<boolean> {
  const token = getSessionToken()?.trim();
  if (token && isAccessTokenValidForApi(token)) {
    return true;
  }
  try {
    const fresh = await obtainFreshAccessViaRefresh();
    if (fresh && isAccessTokenValidForApi(fresh)) {
      persistAccessTokenOnly(fresh);
      return true;
    }
  } catch {
    /* fall through */
  }
  return false;
}

async function resolveFavouritePostCandidates(entry: FavoriteAssetEntry): Promise<string[]> {
  const merged = new Set<string>();
  for (const candidate of rankFavouritePostCandidates(favouriteEntryIdCandidates(entry))) {
    merged.add(candidate);
    try {
      const loaded = await getAssetById(candidate);
      if (isRecord(loaded)) {
        for (const id of favouriteEntryIdCandidates({
          id: candidate,
          asset: coalesceAssetListRecord(loaded),
          savedAt: entry.savedAt,
        })) {
          merged.add(id);
        }
      }
    } catch {
      /* keep list-row ids */
    }
  }
  return rankFavouritePostCandidates(Array.from(merged));
}

function favouriteEntryIdCandidates(entry: FavoriteAssetEntry): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const add = (raw: unknown) => {
    if (typeof raw === 'string' && raw.trim()) {
      const trimmed = raw.trim();
      const key = trimmed.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        out.push(trimmed);
      }
    } else if (typeof raw === 'number' && Number.isFinite(raw)) {
      const trimmed = String(raw);
      const key = trimmed.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        out.push(trimmed);
      }
    }
  };

  const asset = coalesceAssetListRecord(entry.asset);
  add(assetRecordMutationId(asset));
  add(entry.id);
  add(assetRecordPrimaryId(asset));
  for (const key of ['asset_id', 'assetId', 'AssetId', 'id', 'Id', 'UUID', 'uuid'] as const) {
    add(asset[key]);
  }
  return out;
}

/** POST one favourite row, trying every known asset id alias. */
export async function addFavouriteEntryToServer(entry: FavoriteAssetEntry): Promise<boolean> {
  const candidates = await resolveFavouritePostCandidates(entry);
  if (candidates.length === 0) {
    return false;
  }

  const tried = new Set<string>();
  const attempt = async (candidate: string): Promise<boolean> => {
    const trimmed = candidate.trim();
    if (!trimmed) {
      return false;
    }
    const key = trimmed.toLowerCase();
    if (tried.has(key)) {
      return false;
    }
    tried.add(key);
    try {
      await addFavourite(trimmed);
      removePendingFavoriteSyncIds([trimmed, ...candidates, entry.id]);
      return true;
    } catch (err) {
      if (!(err instanceof AssetRequestError) || err.httpStatus !== 400) {
        throw err;
      }
      return false;
    }
  };

  for (const candidate of candidates) {
    if (await attempt(candidate)) {
      return true;
    }
  }

  return false;
}

/**
 * Best-effort push of local favourites onto the server while auth is still available.
 * Used on logout and before wiping browser storage.
 */
export async function flushLocalFavoriteAssetsToServer(): Promise<void> {
  if (!(await ensureAccessTokenForFavoriteFlush())) {
    return;
  }

  const allLocal = combineEntriesForFlush();
  if (allLocal.length === 0) {
    return;
  }

  let serverRows: FavouriteWireRow[] = [];
  try {
    serverRows = await fetchFavourites();
  } catch {
    serverRows = [];
  }

  const missingEntries = localFavouriteEntriesMissingOnServer(allLocal, serverRows);
  if (missingEntries.length === 0) {
    return;
  }

  await Promise.all(
    missingEntries.map(async (entry) => {
      try {
        await addFavouriteEntryToServer(entry);
      } catch {
        /* keep pending / local until next login or retry */
      }
    }),
  );
}
